import { useRef, useState, type ReactElement } from 'react';
import {
  TEMPLATE_CATEGORIES,
  type BoardTemplate,
  type TemplateCategoryPatch,
  type TemplateVisibilityPatch,
} from '@molt/shared';
import { Globe2, Tags, X } from 'lucide-react';

import { Button, Dialog, IconButton, Menu, Notice, type MenuItem } from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  VISIBILITY_CONSEQUENCE_KEYS,
  VISIBILITY_LABEL_KEYS,
} from './BoardVisibilityControl';

type TemplateVisibility = TemplateVisibilityPatch['visibility'];
type TemplateCategory = NonNullable<BoardTemplate['templateCategory']>;

/**
 * ⚠ WIRE VALUES, not copy. These are the `MenuItem.id`s that `Menu`'s
 * `selection: 'radio'` matches against, and the `visibility` the patch body
 * carries. Routing one sends a translated string to the server and
 * `templateVisibilityPatchSchema` answers 422 — a 4xx that reads like an auth
 * bug. They stay English forever.
 */
export const TEMPLATE_VISIBILITY_OPTIONS: readonly TemplateVisibility[] = [
  'private',
  'workspace_visible',
  'public',
];

/**
 * `FE-80.1` — a module-level constant cannot call `t()`, so it holds the
 * **catalogue key** and every consumer resolves it at render.
 * `WorkspaceSwitcher.tsx`'s `ALL_WORKSPACES_KEY` is the shipped precedent and
 * states the reason: a constant holding the *English of the moment* leaves the
 * previous language on screen when a catalogue arrives underneath it, and no
 * test in this repository can see that.
 *
 * ⚠ **The record keys are wire values** (`TEMPLATE_CATEGORIES` from
 * `@molt/shared`, read back by `changeCategory` and sent as `category`); only
 * the values became catalogue keys.
 *
 * ⚠ Renamed from `TEMPLATE_CATEGORY_LABELS` outright, with no deprecated
 * alias, because a full-tree scan found **no** test and **no** barrel
 * (`components/index.ts`) reading it. Its two consumers outside this file are
 * `TemplateGallery.tsx` and `BoardFace.tsx`, both changed with it — the
 * five-file path is tabulated in this slice's brief, §5 finding 1.
 *
 * ⚠ A key held in a constant is **invisible to a `t('…')` grep**;
 * `board-templates.routing.test.ts` reads this table's arms as a second source
 * of routed keys.
 */
export const TEMPLATE_CATEGORY_LABEL_KEYS: Record<TemplateCategory, string> = {
  business: 'boardTemplates.category.business',
  design: 'boardTemplates.category.design',
  education: 'boardTemplates.category.education',
  engineering: 'boardTemplates.category.engineering',
  marketing: 'boardTemplates.category.marketing',
  personal: 'boardTemplates.category.personal',
  product_management: 'boardTemplates.category.productManagement',
  project_management: 'boardTemplates.category.projectManagement',
  remote_work: 'boardTemplates.category.remoteWork',
  sales: 'boardTemplates.category.sales',
  support: 'boardTemplates.category.support',
};

/** Module-private; catalogue keys rather than English, for the reason above. */
const TEMPLATE_VISIBILITY_COPY_KEYS: Record<TemplateVisibility, string> = {
  private: 'boardTemplates.templateVisibility.copy.private',
  workspace_visible: 'boardTemplates.templateVisibility.copy.workspace',
  public: 'boardTemplates.templateVisibility.copy.public',
};

export interface TemplateVisibilityControlsProps {
  readonly template: BoardTemplate;
  readonly canManage: boolean;
  readonly disabledReason: string | null;
  readonly visibilityBusy?: boolean | undefined;
  readonly categoryBusy?: boolean | undefined;
  readonly visibilityError?: string | undefined;
  readonly categoryError?: string | undefined;
  readonly onChangeVisibility: (patch: TemplateVisibilityPatch) => void;
  readonly onChangeCategory: (patch: TemplateCategoryPatch) => void;
  readonly className?: string;
}

function menuLabel(title: string, description: string): ReactElement {
  return (
    <span className="flex flex-col items-start gap-0.5 py-0.5 text-left">
      <span className="text-card text-ink">{title}</span>
      <span className="text-xs text-ink-muted">{description}</span>
    </span>
  );
}

export function TemplateVisibilityControls({
  template,
  canManage,
  disabledReason,
  visibilityBusy,
  categoryBusy,
  visibilityError,
  categoryError,
  onChangeVisibility,
  onChangeCategory,
  className,
}: TemplateVisibilityControlsProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [confirmPublicOpen, setConfirmPublicOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  /* One value read at two sites, exactly as the English constant it replaces
   * was — the dialog's `description` and its body paragraph. Not a collapse. */
  const publicConfirmBody = t('boardTemplates.templateVisibility.confirmBody');
  const visibilityLabel = t(VISIBILITY_LABEL_KEYS[template.visibility]);
  const templateVisibility =
    template.visibility === 'private' ||
    template.visibility === 'workspace_visible' ||
    template.visibility === 'public'
      ? template.visibility
      : null;
  const categoryLabel =
    template.templateCategory === null
      ? t('boardTemplates.templateVisibility.noCategory')
      : t(TEMPLATE_CATEGORY_LABEL_KEYS[template.templateCategory]);
  const controlReason = canManage
    ? null
    : (disabledReason ?? t('boardTemplates.templateVisibility.manageReason'));
  const lockedProps =
    controlReason === null ? {} : { disabled: true, disabledReason: controlReason };
  const visibilityBusyProps =
    visibilityBusy === undefined ? {} : { busy: visibilityBusy };
  const categoryBusyProps = categoryBusy === undefined ? {} : { busy: categoryBusy };

  const visibilityItems: MenuItem[] = TEMPLATE_VISIBILITY_OPTIONS.map((visibility) => ({
    id: visibility,
    label: menuLabel(
      t(VISIBILITY_LABEL_KEYS[visibility]),
      t(TEMPLATE_VISIBILITY_COPY_KEYS[visibility]),
    ),
    onSelect: () => undefined,
  }));
  const categoryItems: MenuItem[] = [
    {
      id: 'none',
      label: menuLabel(
        t('boardTemplates.templateVisibility.noCategory'),
        t('boardTemplates.templateVisibility.noCategoryDescription'),
      ),
      onSelect: () => undefined,
    },
    ...TEMPLATE_CATEGORIES.map((category) => {
      /* One slotted key rather than two adjacent nodes: a translator must be
       * able to move the category name inside the sentence. */
      const label = t(TEMPLATE_CATEGORY_LABEL_KEYS[category]);
      return {
        id: category,
        label: menuLabel(
          label,
          t('boardTemplates.templateVisibility.categoryDescription', {
            category: label,
          }),
        ),
        onSelect: () => undefined,
      };
    }),
  ];

  function changeVisibility(nextVisibility: string): void {
    if (nextVisibility === 'public') {
      setConfirmPublicOpen(true);
      return;
    }
    if (nextVisibility === 'private' || nextVisibility === 'workspace_visible') {
      onChangeVisibility({ visibility: nextVisibility });
    }
  }

  function changeCategory(nextCategory: string): void {
    onChangeCategory({
      category: nextCategory === 'none' ? null : (nextCategory as TemplateCategory),
    });
  }

  const visibilityControl = canManage ? (
    <Menu
      trigger={
        <Button
          type="button"
          variant="secondary"
          aria-label={t('boardTemplates.templateVisibility.trigger', {
            label: visibilityLabel,
          })}
          iconStart={<Globe2 aria-hidden="true" className="size-4" />}
          {...visibilityBusyProps}
        >
          {visibilityLabel}
        </Button>
      }
      sections={[
        {
          id: 'template-visibility',
          selection: 'radio',
          value: templateVisibility ?? '',
          onValueChange: changeVisibility,
          items: visibilityItems,
        },
      ]}
    />
  ) : (
    <Button
      type="button"
      variant="secondary"
      aria-label={t('boardTemplates.templateVisibility.trigger', {
        label: visibilityLabel,
      })}
      iconStart={<Globe2 aria-hidden="true" className="size-4" />}
      {...lockedProps}
    >
      {visibilityLabel}
    </Button>
  );

  const categoryControl = canManage ? (
    <Menu
      trigger={
        <Button
          type="button"
          variant="secondary"
          aria-label={t('boardTemplates.templateVisibility.categoryTrigger', {
            label: categoryLabel,
          })}
          iconStart={<Tags aria-hidden="true" className="size-4" />}
          {...categoryBusyProps}
        >
          {categoryLabel}
        </Button>
      }
      sections={[
        {
          id: 'template-category',
          selection: 'radio',
          value: template.templateCategory ?? 'none',
          onValueChange: changeCategory,
          items: categoryItems,
        },
      ]}
    />
  ) : (
    <Button
      type="button"
      variant="secondary"
      aria-label={t('boardTemplates.templateVisibility.categoryTrigger', {
        label: categoryLabel,
      })}
      iconStart={<Tags aria-hidden="true" className="size-4" />}
      {...lockedProps}
    >
      {categoryLabel}
    </Button>
  );

  return (
    <section
      aria-label={t('boardTemplates.templateVisibility.region')}
      className={cn(
        'flex flex-col gap-3 rounded-card border border-border p-3',
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-ink">
          {t('boardTemplates.templateVisibility.heading')}
        </h3>
        <p className="text-xs text-ink-muted">
          {templateVisibility === null
            ? t(VISIBILITY_CONSEQUENCE_KEYS[template.visibility])
            : t(TEMPLATE_VISIBILITY_COPY_KEYS[templateVisibility])}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {visibilityControl}
        {categoryControl}
      </div>

      {visibilityError !== undefined ? (
        <Notice tone="bad" body={visibilityError} />
      ) : null}
      {categoryError !== undefined ? <Notice tone="bad" body={categoryError} /> : null}

      <Dialog
        open={confirmPublicOpen}
        onOpenChange={(open) => {
          if (!open) setConfirmPublicOpen(false);
        }}
        title={t('boardTemplates.templateVisibility.confirmTitle')}
        description={publicConfirmBody}
        initialFocus={cancelRef}
        closeControl={
          <IconButton
            ref={cancelRef}
            label={t('boardTemplates.templateVisibility.confirmClose')}
            size="sm"
            variant="quiet"
          >
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
        footer={
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setConfirmPublicOpen(false)}
            >
              {t('boardTemplates.templateVisibility.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              iconStart={<Globe2 aria-hidden="true" className="size-4" />}
              onClick={() => {
                setConfirmPublicOpen(false);
                onChangeVisibility({
                  visibility: 'public',
                  confirmWorldReadable: true,
                });
              }}
            >
              {t('boardTemplates.templateVisibility.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">{publicConfirmBody}</p>
      </Dialog>
    </section>
  );
}
